import React from 'react';
import { Batiment } from '../../../../shared/types/game';
import { BUILDING_INTERVENTION_SPAWN_RADIUS_KM } from '../../../../shared/constants/gameMap';
import { useBuildingCapacity } from './useBuildingCapacity';
import { getBuildingIconPath, getBuildingIconConfig } from './buildingDetailHelpers';

interface BuildingInfoTabProps {
  building: Batiment;
  address?: string;
  ctaName?: string;
  calculatedLevel: number;
  totalCapacity: number;
  totalVehiclesCapacity: number;
  timeRemainingPersonnel: number | null;
  timeRemainingVehicules: number | null;
  handleStartAugmentation: (type: 'personnel' | 'vehicules', places?: number) => void;
  onBuildingUpdate?: (updatedBuilding: Batiment) => void;
  isEditingName: boolean;
  editedName: string;
  onStartEditName: () => void;
  onChangeEditedName: (v: string) => void;
  onSaveName: () => void;
  onCancelEditName: () => void;
}

const InfoLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1.5">{children}</p>
);

const BuildingInfoTab: React.FC<BuildingInfoTabProps> = ({
  building,
  address,
  ctaName,
  calculatedLevel,
  totalVehiclesCapacity,
  timeRemainingPersonnel,
  timeRemainingVehicules,
  handleStartAugmentation,
  onBuildingUpdate,
  isEditingName,
  editedName,
  onStartEditName,
  onChangeEditedName,
  onSaveName,
  onCancelEditName,
}) => {
  const {
    formatTimeRemaining,
    BASE_CAPACITY_PER_LEVEL,
    CAPACITY_INCREASE_PER_LEVEL,
    CAPACITY_PER_AUGMENTATION,
    XP_REQUIS_PAR_NIVEAU,
  } = useBuildingCapacity(building, () => {}, undefined);

  const iconPath = getBuildingIconPath(building.type);
  const iconConfig = getBuildingIconConfig(building.type);

  const totalCapa = (building.postes_disponibles ?? 0) + (building.postes_occupes ?? 0);
  const xpCurrent = building.xp ?? 0;
  const xpForLevel = calculatedLevel * XP_REQUIS_PAR_NIVEAU;
  const xpInLevel = xpCurrent % XP_REQUIS_PAR_NIVEAU;
  const xpPct = Math.min(100, Math.round((xpInLevel / XP_REQUIS_PAR_NIVEAU) * 100));
  const xpRemaining = XP_REQUIS_PAR_NIVEAU - xpInLevel;

  const capaPctPersonnel = totalCapa > 0 ? Math.round(((building.postes_occupes ?? 0) / totalCapa) * 100) : 0;
  const capaPctVehicules = totalVehiclesCapacity > 0
    ? Math.round(((building.capacite_vehicules ?? totalVehiclesCapacity) / totalVehiclesCapacity) * 100)
    : 0;

  return (
    <div className="grid grid-cols-[220px_1fr] min-h-[440px] -m-5">

      {/* ── PANNEAU GAUCHE ── */}
      <div className="border-r border-zinc-800 bg-zinc-950/60 flex flex-col">

        {/* Identité */}
        <div className="p-5 border-b border-zinc-800">
          <div
            className="w-14 h-14 rounded-xl flex items-center justify-center border border-white/10 shadow-lg mb-3"
            style={{ backgroundColor: iconConfig.backgroundColor }}
          >
            <img src={iconPath} alt={building.type} className="w-9 h-9" />
          </div>
          {isEditingName ? (
            <input
              type="text"
              value={editedName}
              onChange={(e) => onChangeEditedName(e.target.value)}
              onBlur={onSaveName}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onSaveName();
                else if (e.key === 'Escape') onCancelEditName();
              }}
              className="text-sm font-semibold bg-zinc-800 text-white px-2 py-1 rounded-lg border border-sky-500/60 focus:outline-none focus:ring-2 focus:ring-sky-500/40 w-full mb-1"
              autoFocus
            />
          ) : (
            <button
              type="button"
              onClick={onStartEditName}
              className="group flex items-center gap-1.5 text-left mb-1"
              title="Cliquer pour renommer"
            >
              <span className="text-sm font-semibold text-zinc-100 group-hover:text-sky-300 transition-colors leading-tight">
                {building.nom}
              </span>
              <svg className="w-3 h-3 text-zinc-600 group-hover:text-sky-400 transition-colors shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
          )}
          <p className="text-[11px] text-zinc-500">{building.type === 'pompier' ? 'Caserne de pompiers' : building.type === 'hopital' ? 'Hôpital' : building.type === 'fdo' ? 'Forces de l\'ordre' : building.type === 'cta' ? 'Centre de traitement des alertes' : building.type === 'prison' ? 'Prison' : building.type}</p>
        </div>

        {/* État */}
        <div className="p-5 border-b border-zinc-800">
          <InfoLabel>État du bâtiment</InfoLabel>
          <div className="flex items-center gap-2 mb-1.5">
            <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${building.actif ? 'bg-emerald-400' : 'bg-zinc-500'}`} />
            <span className="text-sm font-medium text-zinc-100">
              {building.actif ? 'Actif' : 'Inactif'} · Niveau {building.niveau}
            </span>
          </div>
          {building.type !== 'cta' && (
            <p className="text-[10px] text-zinc-500 leading-relaxed mb-3">
              Secteur {BUILDING_INTERVENTION_SPAWN_RADIUS_KM} km actif.
              {building.cta_id
                ? ` Lié au ${ctaName ?? `CTA #${building.cta_id}`}.`
                : ' Non lié à un CTA.'}
            </p>
          )}
          {onBuildingUpdate && (
            <button
              onClick={() => onBuildingUpdate({ ...building, actif: !building.actif, updated_at: new Date().toISOString() })}
              className={`w-full rounded-lg py-1.5 text-xs font-medium transition-colors border ${
                building.actif
                  ? 'border-red-500/30 bg-red-500/10 text-red-400 hover:bg-red-500/20'
                  : 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20'
              }`}
            >
              {building.actif ? 'Désactiver le bâtiment' : 'Activer le bâtiment'}
            </button>
          )}
        </div>

        {/* Occupation */}
        {building.type !== 'cta' && (
          <div className="p-5 border-b border-zinc-800">
            <InfoLabel>Occupation</InfoLabel>
            <div className="space-y-3">
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-zinc-400">Personnel</span>
                  <span className="text-zinc-200 font-medium">{building.postes_occupes ?? 0} / {totalCapa}</span>
                </div>
                <div className="h-1 bg-zinc-800 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-500 rounded-full" style={{ width: `${capaPctPersonnel}%` }} />
                </div>
              </div>
              {totalVehiclesCapacity > 0 && (
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-zinc-400">Véhicules</span>
                    <span className="text-zinc-200 font-medium">{building.capacite_vehicules ?? 0} / {totalVehiclesCapacity}</span>
                  </div>
                  <div className="h-1 bg-zinc-800 rounded-full overflow-hidden">
                    <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${capaPctVehicules}%` }} />
                  </div>
                </div>
              )}
              <div className="flex justify-between text-xs">
                <span className="text-zinc-400">Postes libres</span>
                <span className="font-medium text-emerald-400">{building.postes_disponibles ?? 0}</span>
              </div>
            </div>
          </div>
        )}

        {/* Localisation */}
        <div className="p-5 mt-auto">
          <InfoLabel>Localisation</InfoLabel>
          <p className="text-xs text-zinc-300 leading-relaxed mb-2">
            {address ?? building.localisation ?? '—'}
          </p>
          {building.cta_id && (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/25 bg-emerald-500/10 px-2.5 py-1 text-[10px] font-medium text-emerald-400">
              <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
              {ctaName ?? `CTA #${building.cta_id}`}
            </span>
          )}
          {!building.cta_id && building.type !== 'cta' && (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/25 bg-amber-500/10 px-2.5 py-1 text-[10px] font-medium text-amber-400">
              Non lié à un CTA
            </span>
          )}
        </div>
      </div>

      {/* ── PANNEAU DROIT ── */}
      <div className="p-5 flex flex-col gap-5 overflow-y-auto">

        {/* Progression XP */}
        <div>
          <InfoLabel>Progression</InfoLabel>
          <div className="border-l-2 border-blue-500 bg-zinc-900/60 rounded-r-xl px-4 py-3.5 border border-zinc-800/60">
            <div className="flex items-baseline justify-between mb-1">
              <span className="text-xl font-medium text-zinc-100">Niveau {building.niveau}</span>
              <span className="text-xs text-zinc-500">→ Niveau {building.niveau + 1}</span>
            </div>
            <p className="text-sm font-medium text-blue-400 mb-2.5">{xpCurrent} / {xpForLevel} XP</p>
            <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden mb-1.5">
              <div className="h-full rounded-full bg-blue-500" style={{ width: `${xpPct}%` }} />
            </div>
            <p className="text-[10px] text-zinc-600">
              {xpRemaining} XP restants · +1 XP par intervention terminée · Le niveau monte automatiquement
            </p>
          </div>
        </div>

        {/* Augmentations */}
        {building.type !== 'cta' && (
          <div>
            <InfoLabel>Augmentations de capacité</InfoLabel>
            <div className="grid grid-cols-2 gap-3">

              {/* Personnel */}
              <div className={`border-l-2 bg-zinc-900/60 rounded-r-xl px-4 py-3 border border-zinc-800/60 flex flex-col gap-2 ${
                building.augmentation_personnel_en_cours ? 'border-l-amber-500' : 'border-l-violet-500'
              }`}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-medium text-zinc-100">Personnel</p>
                    <p className="text-[10px] text-zinc-500 mt-0.5">+{CAPACITY_PER_AUGMENTATION} postes par augmentation</p>
                  </div>
                  <span className="text-2xl font-medium text-zinc-100">{building.augmentations_capacite_personnel ?? 0}</span>
                </div>
                {building.augmentation_personnel_en_cours && timeRemainingPersonnel ? (
                  <>
                    <div className="flex items-center gap-1.5 text-[10px] text-amber-400">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      En cours — {formatTimeRemaining(timeRemainingPersonnel)}
                    </div>
                    <div className="h-1 bg-zinc-800 rounded-full overflow-hidden">
                      <div className="h-full rounded-full bg-amber-500 animate-pulse" style={{ width: '60%' }} />
                    </div>
                  </>
                ) : (
                  <button
                    onClick={() => handleStartAugmentation('personnel', 1)}
                    className="self-start rounded-lg border border-violet-500/40 bg-violet-500/10 px-3 py-1 text-[11px] font-medium text-violet-400 hover:bg-violet-500/20 transition-colors"
                  >
                    + Augmenter
                  </button>
                )}
              </div>

              {/* Véhicules */}
              <div className={`border-l-2 bg-zinc-900/60 rounded-r-xl px-4 py-3 border border-zinc-800/60 flex flex-col gap-2 ${
                building.augmentation_vehicules_en_cours ? 'border-l-amber-500' : 'border-l-violet-500'
              }`}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-medium text-zinc-100">Véhicules</p>
                    <p className="text-[10px] text-zinc-500 mt-0.5">+{CAPACITY_PER_AUGMENTATION} places par augmentation</p>
                  </div>
                  <span className="text-2xl font-medium text-zinc-100">{building.augmentations_capacite_vehicules ?? 0}</span>
                </div>
                {building.augmentation_vehicules_en_cours && timeRemainingVehicules ? (
                  <>
                    <div className="flex items-center gap-1.5 text-[10px] text-amber-400">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      En cours — {formatTimeRemaining(timeRemainingVehicules)}
                    </div>
                    <div className="h-1 bg-zinc-800 rounded-full overflow-hidden">
                      <div className="h-full rounded-full bg-amber-500 animate-pulse" style={{ width: '60%' }} />
                    </div>
                  </>
                ) : (
                  <button
                    onClick={() => handleStartAugmentation('vehicules', 1)}
                    className="self-start rounded-lg border border-violet-500/40 bg-violet-500/10 px-3 py-1 text-[11px] font-medium text-violet-400 hover:bg-violet-500/20 transition-colors"
                  >
                    + Augmenter
                  </button>
                )}
              </div>

            </div>
          </div>
        )}

        {/* Coût */}
        {building.cout !== undefined && building.cout > 0 && (
          <div>
            <InfoLabel>Coût de construction</InfoLabel>
            <div className="border-l-2 border-zinc-600 bg-zinc-900/60 rounded-r-xl px-4 py-3 border border-zinc-800/60">
              <p className="text-xl font-medium text-zinc-100">{building.cout.toLocaleString('fr-FR')} €</p>
            </div>
          </div>
        )}

        {/* Dates */}
        <div>
          <InfoLabel>Historique</InfoLabel>
          <div className="grid grid-cols-2 gap-3">
            <div className="border-l-2 border-zinc-700 bg-zinc-900/60 rounded-r-xl px-4 py-3 border border-zinc-800/60">
              <p className="text-xs font-medium text-zinc-100">
                {new Date(building.created_at).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
              <p className="text-[10px] text-zinc-500 mt-1">Date de création</p>
            </div>
            {building.updated_at && (
              <div className="border-l-2 border-zinc-700 bg-zinc-900/60 rounded-r-xl px-4 py-3 border border-zinc-800/60">
                <p className="text-xs font-medium text-zinc-100">
                  {new Date(building.updated_at).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </p>
                <p className="text-[10px] text-zinc-500 mt-1">Dernière modification</p>
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default BuildingInfoTab;
